import lightTheme, {
  space,
} from "../../../packages/orcs-design-system/lib/systemtheme.js";
import darkTheme from "../../../packages/orcs-design-system/lib/systemthemeDark.js";
import { getAssembleMarkSvg } from "../../../packages/orcs-design-system/lib/components/Loading/teamFormMark.js";
import {
  circleGradient,
  lineGradient,
} from "../Placeholder/placeholderGradients.js";

/** Matches `appScale.newNavBarSize` / Header `Bar` height. */
export const APP_HEADER_HEIGHT_PX = 64;
/** Matches collapsed `appScale.sideNavSize`. */
export const SIDEBAR_WIDTH_PX = 48;
export const SIDEBAR_NAV_ITEM_COUNT = 8;
export const CONTENT_SPINNER_SIZE_PX = 64;
/** OrcsSideNav NavItem hit target in collapsed rail. */
export const SIDEBAR_ITEM_SIZE_PX = 30;
export const SIDEBAR_ICON_SIZE_PX = 16;
const HEADER_LOGO_SIZE_PX = 42;
const HEADER_SEARCH_HEIGHT_PX = 36;
const HEADER_SEARCH_WIDTH_PX = 480;
const HEADER_TITLE_WIDTH_PX = 168;
const HEADER_TITLE_HEIGHT_PX = 14;
const HEADER_AVATAR_SIZE_PX = 32;

/**
 * @typedef {{ type: "circle", size: number }
 *   | { type: "line", width: number, height: number }
 *   | { type: "search", width: number, height: number }} HeaderItem
 */

/**
 * Header placeholders left to right. React and the static shell both render from this.
 * @type {HeaderItem[]}
 */
export const HEADER_ITEMS = [
  { type: "circle", size: HEADER_LOGO_SIZE_PX },
  {
    type: "line",
    width: HEADER_TITLE_WIDTH_PX,
    height: HEADER_TITLE_HEIGHT_PX,
  },
  {
    type: "search",
    width: HEADER_SEARCH_WIDTH_PX,
    height: HEADER_SEARCH_HEIGHT_PX,
  },
  { type: "circle", size: HEADER_AVATAR_SIZE_PX },
];

const tokens = (theme) => ({
  bg: theme.colors.appBackground,
  surface: theme.colors.surface,
  border: theme.colors.surfaceBorder,
  mark: theme.colors.greyMid,
  circle: circleGradient(theme),
  line: lineGradient(theme),
});

const circle = (size) =>
  `<div class="bs-circle" style="width:${size}px;height:${size}px"></div>`;
const line = (width, height) =>
  `<div class="bs-line" style="width:${width}px;height:${height}px"></div>`;

/**
 * Static, JS-free copy of AppShellSkeleton for first paint. Built from the same
 * constants and theme tokens, and injected into index.html by the Vite plugin.
 */
export const getBootShellHtml = () => {
  const items = Array.from(
    { length: SIDEBAR_NAV_ITEM_COUNT },
    () => `<div class="bs-item">${circle(SIDEBAR_ICON_SIZE_PX)}</div>`
  ).join("");
  const header = HEADER_ITEMS.map((item) => {
    if (item.type === "circle") return circle(item.size);
    if (item.type === "line") return line(item.width, item.height);
    return `<div class="bs-search">${line(item.width, item.height)}</div>`;
  }).join("");
  return `<div id="boot-shell" aria-hidden="true"><div class="bs-header"><div class="bs-bar">${header}</div></div><div class="bs-body"><div class="bs-rail"><div class="bs-rail-items">${items}</div></div><div class="bs-content"><div class="bs-mark">${getAssembleMarkSvg(
    { size: CONTENT_SPINNER_SIZE_PX }
  )}</div></div></div></div>`;
};

export const getBootShellCss = () => {
  const light = tokens(lightTheme);
  const dark = tokens(darkTheme);
  const vars = ({ bg, surface, border, mark, circle: c, line: l }) =>
    `--bs-bg:${bg};--bs-surface:${surface};--bs-border:${border};--bs-mark:${mark};--bs-circle:${c};--bs-line:${l};`;
  return `
#boot-shell{${vars(light)}position:fixed;inset:0;z-index:2147483000;display:flex;flex-direction:column;height:100vh;overflow:hidden;background:var(--bs-bg)}
html.load-dark #boot-shell{${vars(dark)}}
html,body{margin:0}
#boot-shell .bs-header{background:var(--bs-surface);border:1px solid var(--bs-border);width:100%;box-sizing:content-box}
#boot-shell .bs-bar{display:flex;align-items:center;flex-wrap:nowrap;gap:${space.r};padding:0 ${space.r};height:${APP_HEADER_HEIGHT_PX}px;min-height:${APP_HEADER_HEIGHT_PX}px}
#boot-shell .bs-circle{border-radius:50%;flex-shrink:0;background:var(--bs-circle)}
#boot-shell .bs-line{border-radius:8px;flex-shrink:0;background:var(--bs-line)}
#boot-shell .bs-search{flex:1 1 200px;min-width:0;max-width:550px;margin-left:auto}
#boot-shell .bs-body{display:flex;flex:1;min-height:0;align-items:stretch;width:100%}
#boot-shell .bs-rail{background:var(--bs-surface);width:${SIDEBAR_WIDTH_PX}px;min-width:${SIDEBAR_WIDTH_PX}px;max-width:${SIDEBAR_WIDTH_PX}px;flex-shrink:0;border-right:1px solid var(--bs-border)}
#boot-shell .bs-rail-items{display:flex;flex-direction:column;align-items:center;gap:${space.xs};padding:${space.s} ${space.xs}}
#boot-shell .bs-content{display:flex;flex:1;min-width:0;align-items:center;justify-content:center}
#boot-shell .bs-mark{color:var(--bs-mark);opacity:.85;display:flex}
#boot-shell .bs-item{display:flex;align-items:center;justify-content:center;width:${SIDEBAR_ITEM_SIZE_PX}px;height:${SIDEBAR_ITEM_SIZE_PX}px;flex-shrink:0}
`.trim();
};
